<template>
    <CqModal
        :showModal="show"
        @closeModal="closeModal"
        heading="Medewerkergegevens"
        maxWidth="clamp(36rem, 60vw, 100rem)"
        width="auto"
    >
        <InfoGrid class="infoGrid">
            <InfoHeader>Werknemer</InfoHeader><a :href="(username && resource && 'id' in resource) ? (roosterplanningHostUrl + '?pageid=P_SMOELENBOEK_POPUP&username=' + username + '&resourceid=' + resource.id) : undefined" target="_blank">{{ resourceDisplayName }}</a>
            <InfoHeader>Personeelsnummer</InfoHeader><span>{{ resource && resource.employment ? resource.employment.employeeNumber : 'Onbekend' }}</span>
            <template v-for="phoneNumber in phoneNumbers" :key="phoneNumber">
                <InfoHeader>Telefoonnummer</InfoHeader><span>{{ phoneNumber }}</span>
            </template>
            <template v-if="phoneNumbers.length === 0">
                <InfoHeader>Telefoonnummer</InfoHeader><span><i>Geen telefoonnummers bekend</i></span>
            </template>
            <InfoHeader>E-mailadres</InfoHeader><span>{{ resource?.employment?.user?.email ?? 'Onbekend' }}</span>
            <InfoHeader>In dienst</InfoHeader><span>{{ resource ? formatDate(resource.beginDate) : 'Onbekend' }}</span>
            <template v-if="resourceHasEndDate">
                <InfoHeader>Uit dienst</InfoHeader><span>{{ resourceEndDate }}</span>
            </template>
            <template v-if="resource?.functions?.length">
                <InfoHeader>Functie<template v-if="resource?.functions?.length > 1">s</template></InfoHeader>
                <ul class="noMargin">
                    <li v-for="func in resource.functions" :key="func.id">{{ func.name }}</li>
                </ul>
            </template>
            <template v-if="resource?.atwIssues && resource?.atwIssues.length">
                <InfoHeader>ATW conflicten</InfoHeader>
                <ul class="noMargin">
                    <li v-for="(issue, index) in resource.atwIssues" :key="issue.message + '--' + index">
                        {{ issue.message }}
                    </li>
                </ul>
            </template>
        </InfoGrid>
        <template v-if="resource">
            <h3>Contracten</h3>
            <CqList v-model="contractHeaders" sortable class="list">
                <template v-for="contract in sortedContracts" :key="'contract-' + contract.id">
                    <span>{{ contract.workingHoursPerWeek / 3600 }}</span>
                    <span v-if="contract.maxHoursPerWeek > 0 ">
                        {{ contract.maxHoursPerWeek / 3600 }}
                    </span>
                    <span v-else>
                        -
                    </span>
                    <span>{{ getContractBeginDate(contract) }}</span>
                    <span>{{ getContractEndDate(contract) }}</span>
                    <span>{{ getRosterName(contract.rosterId) }}</span>
                </template>
            </CqList>
            <h3>Verlofaanvragen</h3>
            <template v-if="resource.leavePeriodsFromForms!.length === 0">
                Geen verlofaanvragen
            </template>
            <CqList v-model="leaveHeaders" sortable class="list" v-else>
                <template v-for="leave in sortedLeaveRequests" :key="'leave-' + leave.id">
                    <span>{{ leave.leaveFullName }}</span>
                    <span class="leaveRequestState">{{ leave.stateDisplayText }}</span>
                    <span>{{ formatDate(leave.beginDate) }}</span>
                    <span>{{ formatDate(leave.endDate) }}</span>
                </template>
            </CqList>
        </template>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqList, CqModal, CqListUtil, CqListHeaderDefinition} from '@cquential/ui';
    import Dates from '@cquential/date-utils';
    import {ContractedTime, LeavePeriodFromForm} from '@cquential/api';
    import {DEFAULT_END_DATE} from '@/util/Defaults';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import RoosterplanningUrlBuilder from '@/util/RoosterplanningUrlBuilder';

    @Component({
        components: {
            CqList,
            CqModal,
            InfoGrid,
            InfoHeader,
        },
    })
    export default class ResourceDetailsModal extends Vue {
        private static instance: ResourceDetailsModal | null;

        @Prop({required: true}) public rosterNames!: Record<number, string>;

        public contractHeaders: CqListHeaderDefinition<ContractedTime & {rosterName: string}>[] = [
            {
                label: 'Uren',
                identifier: 'workingHoursPerWeek',
            },
            {
                label: 'Max. uren per week',
                identifier: 'maxHoursPerWeek',
            },
            {
                label: 'Ingangsdatum',
                identifier: 'beginDate',
            },
            {
                label: 'Geldig t/m',
                identifier: 'endDate',
            },
            {
                label: 'Rooster',
                identifier: 'rosterName',
            },
        ];

        public leaveHeaders: CqListHeaderDefinition<LeavePeriodFromForm>[] = [
            {
                label: 'Type',
                identifier: 'leaveFullName',
            },
            {
                label: 'Status',
                identifier: 'stateDisplayText',
            },
            {
                label: 'Vanaf',
                identifier: 'beginDate',
            },
            {
                label: 'Tot en met',
                identifier: 'endDate',
            },
        ];

        public show: boolean = false;
        public resource: ResourceWithKeyedByDateObjects | null = null;
        public username: string | null = null;

        public mounted(): void {
            ResourceDetailsModal.instance = this;
        }

        public beforeUnmount(): void {
            ResourceDetailsModal.instance = null;
        }

        private formatDate(date: Date): string {
            return Dates.Formatter.formatDate(date);
        }

        private getRosterName(rosterId: number): string {
            return this.rosterNames[rosterId] || 'Onbekend';
        }

        public getContractBeginDate(contract: ContractedTime): string {
            const beginDate: string = this.formatDate(contract.beginDate);
            if (beginDate === '01-01-1970') { // TODO replace with decent check (instead of hardcoded date)
                return '-';
            }

            return beginDate;
        }

        public getContractEndDate(contract: ContractedTime): string {
            const endDate: string = this.formatDate(contract.endDate);
            if (endDate === '31-12-2029') { // TODO replace with decent check (instead of hardcoded date)
                return '-';
            }

            return endDate;
        }

        public get resourceHasEndDate(): boolean {
            return !this.resource || this.resource.endDate.getTime() !== DEFAULT_END_DATE;
        }

        public get resourceEndDate(): string {
            // TODO why is there a getter for this if it's only used once? For the begin date, the situation is EXACTLY the same, and no getter is used.
            return this.resource ? this.formatDate(this.resource.endDate) : 'Onbekend';
        }

        public get resourceDisplayName(): string {
            return ResourceUtil.displayName(this.resource)!;
        }

        public get phoneNumbers(): string[] {
            const phoneNumbers: string[] = [];

            if (this.resource?.employment?.user?.phone) {
                phoneNumbers.push(this.resource.employment.user.phone);
            }

            if (this.resource?.employment?.user?.mobilePhone) {
                phoneNumbers.push(this.resource.employment.user.mobilePhone);
            }

            return phoneNumbers;
        }

        public get sortedContracts(): (ContractedTime & {rosterName: string})[] {
            if (!this.resource || !this.resource.contractedTimes) {
                return [];
            }

            return CqListUtil.sortData(
                this.resource.contractedTimes.map((contractedTime: ContractedTime): ContractedTime & {rosterName: string} => ({
                    ...contractedTime,
                    rosterName: this.getRosterName(contractedTime.rosterId),
                })),
                this.contractHeaders,
            );
        }

        public get sortedLeaveRequests(): LeavePeriodFromForm[] {
            if (!this.resource || !this.resource.leavePeriodsFromForms) {
                return [];
            }

            return CqListUtil.sortData(this.resource.leavePeriodsFromForms, this.leaveHeaders);
        }

        public get roosterplanningHostUrl(): string {
            return RoosterplanningUrlBuilder.getHostUrl() ?? '';
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(openResource: ResourceWithKeyedByDateObjects, openUsername: string): void {
            this.instance!.resource = openResource;
            this.instance!.username = openUsername;
            this.instance!.show = true;
        }
    }
</script>

<style lang="scss" scoped>
     .infoGrid * {
         user-select: text;
     }

    .noMargin {
        margin: 0;
        padding-left: 1.2rem;
    }

    .list span {
        padding: .25rem 1rem .25rem .25rem;
    }

    .leaveRequestState {
        text-transform: capitalize;
    }
</style>
